import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { AppConfigService } from '@services/app-configuration.service';
import { AppLoggerService } from '@services/app-logger.service';
import { EventManagerService } from '@services/event-manager.service';
import { defaultConfig } from 'src/app/app.config';

const { configLoaded } = defaultConfig.appEvents;
const gMapsGeolocUrl = `https://maps.googleapis.com/maps/api/geocode/json`;

interface GeocodingResult {
  formatted_address: string;
  geometry: { location: { lat: number; lng: number } };
}

interface GeocodingResponse {
  status: string;
  results: GeocodingResult[];
}

@Injectable({
  providedIn: 'root',
})
export class GeocodeService {
  private _http = inject(HttpClient);
  private _logger = inject(AppLoggerService);
  protected _appConfig = inject(AppConfigService);
  protected _eventManager = inject(EventManagerService);

  address = '';
  lat = 0;
  lng = 0;
  locationResult = '';
  googleApiKey = '';

  constructor() {
    const _eventManager = this._eventManager;

    _eventManager.subscribe(configLoaded.name, () => this.initGeocoder());
  }

  initGeocoder() {
    const googleApiKey = this._appConfig.getParameter('googleApiKey');
    if (googleApiKey) {
      this.googleApiKey = googleApiKey;
    }
  }

  async getCurrentPosition(): Promise<{ latitude: number; longitude: number }> {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
          },
          error => {
            reject('Error obteniendo la ubicación: ' + error.message);
          }
        );
      } else {
        reject('Geolocalización no soportada en este navegador.');
      }
    });
  }

  async geocodeLatLng(lat: number, lng: number): Promise<{ address: string } | null> {
    try {
      const response = await lastValueFrom(
        this._http.get<GeocodingResponse>(gMapsGeolocUrl, {
          params: { latlng: `${lat},${lng}`, key: this.googleApiKey },
        })
      );
      if (response.status === 'OK') {
        return { address: response.results[0].formatted_address };
      } else {
        this._logger.error('Error en geocodificación inversa:', response.status);
        return null;
      }
    } catch (error) {
      this._logger.error('Error en la solicitud de geocodificación inversa:', error);
      return null;
    }
  }

  async getLocation(address: string): Promise<{ latitude: number; longitude: number; address: string } | null> {
    try {
      const response = await lastValueFrom(
        this._http.get<GeocodingResponse>(gMapsGeolocUrl, { params: { address, key: this.googleApiKey } })
      );
      if (response.status === 'OK') {
        const location = response.results[0].geometry.location;
        const formattedAddress = response.results[0].formatted_address;
        return { latitude: location.lat, longitude: location.lng, address: formattedAddress };
      } else {
        this._logger.error('Error en geocodificación:', response.status);
        return null;
      }
    } catch (error) {
      this._logger.error('Error en la solicitud de geocodificación:', error);
      return null;
    }
  }
}
